import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adsApi } from '@/api/client';
import type { EntityCrossFilter, EntityGraph } from '@/utils/entityCrossFilter';
import {
  buildKeywordCatalogLookup,
  enrichSearchTermRows,
  parseSearchTermRowsPayload,
  type SearchTermEnrichedRow,
} from '@/utils/workspaceSearchTermMetrics';
import type { WorkspacePageSize } from '@/hooks/useServerPagination';
import type { MetricFilterPayload } from '@/utils/workspaceMetricFilters';

export type SearchTermPageFilters = {
  search: string;
  matchType: string;
  negated: string;
};

const AD_PRODUCT_KEY_ALIASES: Record<string, string> = {
  SPONSORED_PRODUCTS: 'SP',
  SPONSORED_BRANDS: 'SB',
  SPONSORED_DISPLAY: 'SD',
};

/** Extract an Amazon campaign id from `SP-123`, `sponsored_products::123`, or a raw id. */
export function campaignIdFromWorkspaceKey(raw: string): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  for (const sep of ['::', '-'] as const) {
    const idx = s.indexOf(sep);
    if (idx <= 0) continue;
    const prefix = s.slice(0, idx).trim().toUpperCase();
    const rest = s.slice(idx + sep.length);
    if (!rest) continue;
    if (prefix === 'SP' || prefix === 'SB' || prefix === 'SD') return rest;
    if (AD_PRODUCT_KEY_ALIASES[prefix]) return rest;
  }
  return s;
}

export function campaignIdsFromAppliedFilter(
  appliedFilter: { campaignKeys?: unknown } | null | undefined
): string[] {
  const keys = appliedFilter?.campaignKeys;
  if (!Array.isArray(keys)) return [];
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const key of keys) {
    const id = campaignIdFromWorkspaceKey(String(key ?? ''));
    if (id && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

export function buildWorkspaceSearchTermsPageBody(args: {
  startDate: string;
  endDate: string;
  limit: number;
  offset: number;
  columnFilters?: {
    search?: string;
    matchType?: string;
    negated?: string;
  };
  appliedFilter: EntityCrossFilter | Record<string, unknown>;
  metricFilters?: MetricFilterPayload[];
}) {
  const campaignIds = campaignIdsFromAppliedFilter(args.appliedFilter);
  return {
    startDate: args.startDate,
    endDate: args.endDate,
    limit: args.limit,
    offset: args.offset,
    columnFilters: args.columnFilters,
    appliedFilter: args.appliedFilter as Record<string, unknown>,
    ...(campaignIds.length > 0 ? { campaignIds } : {}),
    ...(args.metricFilters && args.metricFilters.length > 0
      ? { metricFilters: args.metricFilters }
      : {}),
  };
}

export function workspaceSearchTermsPageQueryKey(args: {
  profileId: string;
  startDate: string;
  endDate: string;
  pageSize: number;
  offset: number;
  appliedFilter: EntityCrossFilter;
  search?: string;
  matchType?: string;
  negated?: string;
  metricFilters?: MetricFilterPayload[];
}) {
  return [
    'workspace-search-term-page',
    args.profileId,
    args.startDate,
    args.endDate,
    args.pageSize,
    args.offset,
    args.appliedFilter,
    args.search ?? '',
    args.matchType ?? '',
    args.negated ?? '',
    args.metricFilters ?? [],
  ] as const;
}

export function useWorkspaceSearchTermsPage({
  profileId,
  startDate,
  endDate,
  previousStartDate,
  previousEndDate,
  compareEnabled,
  graph,
  appliedFilter,
  page,
  pageSize,
  filters,
  metricFilters,
  enabled,
}: {
  profileId: string;
  startDate: string;
  endDate: string;
  previousStartDate: string;
  previousEndDate: string;
  compareEnabled: boolean;
  graph: EntityGraph | null;
  appliedFilter: EntityCrossFilter;
  page: number;
  pageSize: WorkspacePageSize;
  filters: SearchTermPageFilters;
  metricFilters?: MetricFilterPayload[];
  enabled: boolean;
}) {
  const offset = (page - 1) * pageSize;
  const searchParam = filters.search.trim() || undefined;
  const matchTypeParam =
    filters.matchType && filters.matchType !== 'ALL' ? filters.matchType : undefined;
  const canFetch = Boolean(profileId) && enabled;
  const columnFilters = {
    search: searchParam,
    matchType: matchTypeParam,
    negated: filters.negated !== 'ALL' ? filters.negated : undefined,
  };

  const currentQuery = useQuery({
    queryKey: workspaceSearchTermsPageQueryKey({
      profileId,
      startDate,
      endDate,
      pageSize,
      offset,
      appliedFilter,
      search: searchParam,
      matchType: matchTypeParam,
      negated: columnFilters.negated,
      metricFilters,
    }),
    queryFn: async () => {
      const res = await adsApi.getWorkspaceSearchTermsPage(
        profileId,
        buildWorkspaceSearchTermsPageBody({
          startDate,
          endDate,
          limit: pageSize,
          offset,
          columnFilters,
          appliedFilter,
          metricFilters,
        })
      );
      if (!res.success) throw new Error('Failed to load search term report metrics');
      return parseSearchTermRowsPayload(res.data);
    },
    enabled: canFetch,
  });

  const previousQuery = useQuery({
    queryKey: workspaceSearchTermsPageQueryKey({
      profileId,
      startDate: previousStartDate,
      endDate: previousEndDate,
      pageSize,
      offset,
      appliedFilter,
      search: searchParam,
      matchType: matchTypeParam,
      negated: columnFilters.negated,
      metricFilters,
    }),
    queryFn: async () => {
      const res = await adsApi.getWorkspaceSearchTermsPage(
        profileId,
        buildWorkspaceSearchTermsPageBody({
          startDate: previousStartDate,
          endDate: previousEndDate,
          limit: pageSize,
          offset,
          columnFilters,
          appliedFilter,
          metricFilters,
        })
      );
      if (!res.success) throw new Error('Failed to load previous search term metrics');
      return parseSearchTermRowsPayload(res.data);
    },
    enabled: canFetch && compareEnabled,
  });

  const campaignNameById = useMemo(() => {
    const map = new Map<string, string>();
    if (!graph) return map;
    for (const campaign of graph.campaigns) {
      map.set(campaign.campaignId, campaign.name);
    }
    return map;
  }, [graph]);

  const adGroupNameById = useMemo(() => {
    const map = new Map<string, string>();
    if (!graph) return map;
    for (const ag of graph.adGroups) {
      map.set(ag.adGroupId, ag.name);
    }
    return map;
  }, [graph]);

  const keywordCatalogLookup = useMemo(() => {
    if (!graph) return buildKeywordCatalogLookup([]);
    const rows: Array<{ keyword_id?: string; keyword_text?: string; match_type?: string }> = [];
    for (const kw of graph.keywords) {
      for (const source of kw.sources) {
        rows.push({
          keyword_id: source.keyword_id != null ? String(source.keyword_id) : undefined,
          keyword_text: source.keyword_text,
          match_type: source.match_type,
        });
      }
    }
    return buildKeywordCatalogLookup(rows);
  }, [graph]);

  const rows = useMemo(() => {
    return enrichSearchTermRows(
      currentQuery.data?.rows ?? [],
      null,
      campaignNameById,
      adGroupNameById,
      keywordCatalogLookup,
      []
    );
  }, [
    currentQuery.data?.rows,
    campaignNameById,
    adGroupNameById,
    keywordCatalogLookup,
  ]);

  const previousByKey = useMemo(() => {
    const map = new Map<string, SearchTermEnrichedRow>();
    if (!compareEnabled) return map;
    const enriched = enrichSearchTermRows(
      previousQuery.data?.rows ?? [],
      null,
      campaignNameById,
      adGroupNameById,
      keywordCatalogLookup,
      []
    );
    for (const row of enriched) map.set(row.rowKey, row);
    return map;
  }, [
    compareEnabled,
    previousQuery.data?.rows,
    campaignNameById,
    adGroupNameById,
    keywordCatalogLookup,
  ]);

  const totalCount = currentQuery.data?.totalCount ?? 0;

  return {
    rows,
    previousByKey,
    totalCount,
    isLoading: currentQuery.isLoading,
    isPending: currentQuery.isPending,
    isFetching: currentQuery.isFetching,
    error: currentQuery.error,
    refetch: () => {
      void currentQuery.refetch();
      void previousQuery.refetch();
    },
  };
}
